import React, { useState } from 'react';
import { ExternalLink, Github, ArrowRight, Layers } from 'lucide-react';

// Mock data - replace with your actual data
const projects = [
  {
    id: 1,
    title: "Versión Vercel",
    subtitle: "Landing Page Institucional",
    description: "Versión moderna y optimizada de la landing page institucional de la Fundación Cardenal Garibi Rivera, desplegada en Vercel con tecnologías de vanguardia y diseño responsive.",
    image: "/screenshots/landing1.png",
    tags: ["React", "Next.js", "Vercel", "Tailwind CSS"],
    url: "https://vercel.fundaciongaribirivera.com/",
    github: "https://github.com/fundaciongaribirivera/vercel-version",
    featured: false
  },
  {
    id: 2,
    title: "Versión 2025-12",
    subtitle: "Landing Page Institucional",
    description: "Versión actualizada de la landing page institucional con nuevas funcionalidades, diseño renovado y optimizaciones para el año 2025, enfocada en la experiencia del usuario.",
    image: "/screenshots/landing2.png",
    tags: ["React", "TypeScript", "Node.js", "PostgreSQL"],
    url: "https://202512.fundaciongaribirivera.com/",
    github: "https://github.com/fundaciongaribirivera/2025-version",
    featured: false
  }
];

const ProjectCard = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="glass-panel rounded-2xl overflow-hidden h-full transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
        {/* Image Container */}
        <div className="relative overflow-hidden h-64">
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-10" />
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />

          {/* Quick Actions */}
          <div className={`absolute top-4 right-4 z-20 flex gap-2 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-background/80 backdrop-blur-md rounded-full hover:bg-background transition-colors border border-white/20"
              aria-label="Ver código en GitHub"
            >
              <Github className="w-5 h-5 text-foreground" />
            </a>
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-primary/90 backdrop-blur-md rounded-full hover:bg-primary transition-colors"
              aria-label="Visitar proyecto"
            >
              <ExternalLink className="w-5 h-5 text-primary-foreground" />
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Title & Subtitle */}
          <div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
              {project.title}
            </h3>
            <p className="text-sm font-medium text-primary uppercase tracking-wider">
              {project.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className="text-muted-foreground leading-relaxed text-sm">
            {project.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 pt-2">
            {project.tags.map((tag, i) => (
              <span
                key={i}
                className="px-3 py-1 text-xs font-medium bg-secondary/50 text-secondary-foreground rounded-full border border-border/50 hover:bg-secondary transition-colors"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* URL Display */}
          <div className="pt-4 border-t border-border/50">
            <div className="flex items-center justify-between gap-2">
              <code className="text-xs text-muted-foreground font-mono truncate">
                {project.url}
              </code>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-4 h-4 text-primary flex-shrink-0 transition-transform hover:translate-x-1 hover:scale-110 cursor-pointer"
                aria-label="Visitar proyecto"
              >
                <ArrowRight className="w-full h-full" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PortfolioPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/10">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(47,90,55,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(240,180,41,0.05),transparent_50%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img
                src="/logos/ico.png"
                alt="Fundación Cardenal Garibi Rivera"
                className="h-20 w-auto"
              />
            </div>



            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground text-balance leading-tight">
              Fundación Cardenal{' '}
              <span className="text-primary">Garibi Rivera</span>
            </h1>
            {/* Icon Badge */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full">
                <Layers className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">
                  Portafolio de Landing Pages
                </span>
              </div>
            </div>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Explora las diferentes versiones de la landing page institucional.
            </p>
            

            {/* Decorative Element */}
            <div className="flex justify-center gap-2 pt-4">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <div className="w-2 h-2 rounded-full bg-primary/30" />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        {/* Additional Info Section */}
        <div className="mt-16 glass-panel rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/logos/ico.png"
              alt="Fundación Cardenal Garibi Rivera"
              className="h-16 w-auto mb-4"
            />
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-3">
            Fundación Cardenal Garibi Rivera
          </h3>
        </div>
      </div>
    </div>
  );
}
